import { KeyboardEvent, ClipboardEvent, useState, useEffect} from 'react';
import { resendActivationEmail, updateEmail } from '../../dal/data-provider';
import { IServerValidation } from '../../../../domain/validation/interfaces';
import ValidationErrors from '../../../../domain/validation/server-errors/validation-errors';
import {
    createControl,
    updateControl,
    validateForm,
    removeInvisibleCharsOnPaste,
    validateControl
} from '../../../../domain/validation/validation';
import { getIsAuthenticated, getIsChina } from '../../../../domain/hidden-data-provider';
import LogoutScreen from '../../logout/logout-screen';
import { timeout } from '../../../../domain/common';
import { IFormControl } from '../../../../ui/forms/interfaces';
import FormBorder from '../../../../ui/forms/form-border';
import FormTextField from '../../../../ui/forms/fields/text-field';
import FormPreloader from '../../../../ui/forms/form-preloader';
import { getSimpleCaptchaValidationError } from '../../../../domain/captcha/simple-captcha';
import {
    getRecaptcha2Error,
    getRecaptchaToken,
    renderRecaptcha2,
    resetRecaptcha2
} from '../../../../domain/captcha/recaptcha-provider';
import ChinaCaptchaField from '../../../../ui/forms/fields/captcha-china-field';
import Recaptcha3Text from '../../../../domain/captcha/recaptcha3-terms';

const TIMEOUT_MS = 3000;

export interface IAppData {
    userId: number,
    email: string,
    isCMS: boolean,
}

interface IAppProps{
    data: IAppData|null,
}

/**
 * Activate Account App
 * @constructor
 */
const App = (props: IAppProps) => {

    const isUserAuth = getIsAuthenticated();
    const email = props?.data?.email;

    // validation ---------------------------------------
    const newEmailNameControl = createControl('new-email', email, {
        required: true,
        email: true,
    });

    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });

    const [isUpdateEmailLoading, setIsUpdateEmailLoading] = useState(false);
    const [isResendEmailLoading, setIsResendEmailLoading] = useState(false);
    const [updateEmailResponse, setUpdateEmailResponse] = useState<IServerValidation|null>();
    const [resendEmailResponse, setResendEmailResponse] = useState<IServerValidation|null>();
    const [newEmail, setNewEmail] = useState<IFormControl>(newEmailNameControl);
    const [isUpdateEmailFormVisible, setIsUpdateEmailFormVisible] = useState(false);
    const [okMessage, setOkMessage] = useState('');

    // simple captcha - used for china
    const isChina = getIsChina();
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    useEffect(() => {

        // if session is empty and doesn't contain registered user details -> redirect to login page
        if(props !== null && props.data !== null && props.data.email && !props.data.isCMS) return;

        // redirect to login page
        window.location.href = '/login';
    }, [props]);

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    /**
     * ok message
     */
    const getOkMessage = (email: string) => {
        return `An account activation link has been sent to ${ email }`;
    };

    /**
     * show OK message
     */
    const showMessage = async (email: string|undefined) => {
        if(!email) return;

        setOkMessage(getOkMessage(email!));
        await timeout(TIMEOUT_MS);
        setOkMessage('');
    };

    /**
     * resend activation email
     */
    const performResendActivationEmail = async () => {

        setResendEmailResponse(null);
        setUpdateEmailResponse(null);
        setOkMessage('');
        setCaptchaError('');

        // client side validation
        const isValid =
            isChina ?
                validateForm([
                    [simpleCaptchaCode, setSimpleCaptchaCode],
                ]) : true;

        if(!isValid) return;

        setIsResendEmailLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsResendEmailLoading(false);
                return;
            }

            response = await resendActivationEmail(captchaToken, recaptchaVersion);
        }
        catch(ex){}

        setIsResendEmailLoading(false);

        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid){

            // all is well ---------------

            // show OK message
            showMessage(email);

            return;
        }

        // handle errors ---------
        setResendEmailResponse(response?.data);
        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }
    };

    /**
     * update email
     */
    const performUpdateEmail = async () => {

        setResendEmailResponse(null);
        setUpdateEmailResponse(null);
        setOkMessage('');
        setCaptchaError('');

        // client side validation
        const isValid =
            isChina ?
                validateForm([
                    [newEmail, setNewEmail],
                    [simpleCaptchaCode, setSimpleCaptchaCode],
                ]) :
                validateForm([
                    [newEmail, setNewEmail],
                ]);

        if(!isValid) return;

        setIsUpdateEmailLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsUpdateEmailLoading(false);
                return;
            }

            response = await updateEmail(
                newEmail.value,
                captchaToken,
                recaptchaVersion,
            );
        }
        catch(ex){}

        setIsUpdateEmailLoading(false);

        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid){

            // all is well ---------------

            // show OK message
            showMessage(newEmail.value);

            return;
        }

        // handle errors ---------
        setUpdateEmailResponse(response?.data);

        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }
    };

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await performUpdateEmail();
        }
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResendEmailResponse(null);
        setUpdateEmailResponse(null);
        setOkMessage('');
        setCaptchaError('');
    };

    /**
     * on 'update form visibility' button click
     */
    const handleUpdateEmailVisibleClick = () => {
        setIsUpdateEmailFormVisible(isVisible => {
            return !isVisible;
        });
    };

    return (
        <>
            {
                !isUserAuth &&
                <FormBorder>
                    <div className="text-center text-gray-700">
                        <h1 className="text-2xl font-bold mb-6">Just one more step...</h1>
                        <p data-qa="activate-account-success-msg">To activate your account, click the link in the email we've sent to:</p>
                        <p className="font-bold mb-6" data-qa="activate-account-main-email">{ email }</p>

                        {
                            /* ------------- google recaptcha version 2 or 3 ------------- */
                            !isChina &&
                            <div id="recaptcha" className={`mb-4 flex justify-center recaptcha recaptcha-version-${ recaptchaVersion }`}/>
                        }

                        {
                            /* ------------- china simple captcha ------------- */
                            isChina &&
                            <ChinaCaptchaField
                                control={ simpleCaptchaCode }
                                setSimpleCaptchaCode={ setSimpleCaptchaCode }
                                simpleCaptchaAttempts={ simpleCaptchaAttempts }
                            />
                        }

                        <p>Didn't receive the email?</p>
                        <p className="mb-6">Click to <button
                            type="button"
                            className="text-blue-200 underline text-base cursor-pointer"
                            onClick={ performResendActivationEmail }
                        >resend the activation email</button></p>

                        {
                            isResendEmailLoading && <div className="mb-8">
                                <FormPreloader />
                            </div>
                        }

                        {
                            resendEmailResponse && !resendEmailResponse.isValid && (
                                <div className="text-sm text-red my-4 text-center" role="alert">
                                    <ValidationErrors errors={ resendEmailResponse.errors } />
                                </div>
                            )
                        }


                        <p>Wrong email address?</p>
                        <p className="mb-6"><button
                            data-qa="activate-account-change-email"
                            type="button"
                            className="text-blue-200 underline text-base cursor-pointer"
                            onClick={ handleUpdateEmailVisibleClick }>Enter the correct email</button></p>
                    </div>

                    {
                        isUpdateEmailFormVisible &&
                        <>
                            <FormTextField
                                id={ 'new-email-tb' }
                                label={ 'Enter Your Email' }
                                qa="activate-account-email"
                                control={ newEmail }
                                maxLength={ 100 }
                                focus={ true }
                                onChange={ evt => {
                                    const $target = evt.target as HTMLInputElement;
                                    setNewEmail(updateControl(newEmail, $target.value, !newEmail.isValid));
                                    setUpdateEmailResponse(null); // empty server error
                                    setResendEmailResponse(null);
                                    setCaptchaError('');
                                }}
                                onBlur={ evt => {
                                    setNewEmail(validateControl(newEmail));
                                }}
                                onClick={ handleFieldClick }
                                onKeyUp={ handleEnter }
                                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                    setNewEmail(updateControl(newEmail, removeInvisibleCharsOnPaste(evt)));
                                }}
                            />

                            <div className="mb-2">

                                { isUpdateEmailLoading && <FormPreloader /> }

                                {
                                    !isUpdateEmailLoading &&
                                    <button
                                        data-qa="activate-account-submit-email"
                                        className="w-btn"
                                        type="button"
                                        onClick={async evt => {
                                            await performUpdateEmail();
                                        }}
                                    >Update Email</button>
                                }
                            </div>

                            {
                                updateEmailResponse && !updateEmailResponse.isValid && (
                                    <div className="text-sm text-red my-4 text-center" role="alert">
                                        <ValidationErrors errors={ updateEmailResponse.errors } />
                                    </div>
                                )
                            }
                        </>
                    }

                    {
                        okMessage &&
                        <div className="text-center text-green">{ okMessage }</div>
                    }

                    {
                        captchaError &&
                        <div className="text-sm text-red my-4 text-center" data-id={ recaptcha2WidgetID } role="alert">{ captchaError }</div>
                    }

                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className="mt-8 text-center">
                            <Recaptcha3Text />
                        </div>
                    }

                </FormBorder>
            }

            { isUserAuth && <LogoutScreen /> }
        </>

    )
};

export default  App;